import { colors } from '../components/theme/theme';

import type { PaymentHistoryItemNew } from '../services/payment-services';

/**
 * Shared by the contract-scoped and global payment-history pages: maps the status-chip label
 * (case-insensitive) to its background color. Any status not explicitly listed falls back to grey.
 */
export const getPaymentStatusChipColor = (status: string): string => {
    switch (status?.toLowerCase()) {
        case 'completed':
            return colors.successPreset[30];
        case 'pending':
            return colors.warningPreset[50];
        case 'returned':
            return colors.errorPreset[10];
        default:
            return colors.greyPreset[30];
    }
};

/**
 * Shared by the contract-scoped and global payment-history pages: sorts a list of date-bearing
 * items most-recent-first, using the same `new Date(...).getTime()` comparison in both places.
 * Missing/blank dates behave exactly like `new Date('').getTime()` (NaN, sorts as if invalid),
 * matching the original inline implementations.
 */
export const sortByDateDesc = <T>(items: T[], getDateString: (item: T) => string | undefined): T[] => {
    return [...items].sort((a, b) => {
        const dateA = getDateString(a) || '';
        const dateB = getDateString(b) || '';
        return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
};

/**
 * Common shape produced by mapPaymentHistoryRow — the fields both payment-history pages compute
 * identically from a raw PaymentHistoryItemNew.
 */
export interface PaymentHistoryRowBase {
    [key: string]: unknown;
    contractId: string;
    paymentDate: string;
    invoiceNumber: string;
    referenceNumber: string;
    paymentType: string;
    amount: string;
    invoices: PaymentHistoryItemNew['invoices'];
    invoiceCount: number;
    status: string;
    originalPayment: PaymentHistoryItemNew;
}

/**
 * Shared by the contract-scoped and global payment-history pages: transforms a raw payment history
 * item into the row shape used by CommonTable. `formatToUSDate`/`formatCurrency` are passed in so
 * this helper doesn't need to duplicate (or risk drifting from) each page's formatting imports.
 */
/**
 * Shared by the contract-scoped and global payment-history pages: builds the `sx` override that
 * makes every CommonTable column (other than the leading accordion-toggle column) share the
 * remaining width equally. Both pages pass `columns.length` from their own column definitions.
 */
export const getEqualWidthTableSx = (columnCount: number) => ({
    '& .MuiTable-root': {
        tableLayout: 'fixed',
        width: '100%',
    },
    '& .MuiTable-root .MuiTableHead .MuiTableCell-root:not(:first-of-type)': {
        width: `calc((100% - 50px) / ${columnCount})`,
    },
    '& .MuiTable-root .MuiTableBody .MuiTableCell-root:not(:first-of-type)': {
        width: `calc((100% - 50px) / ${columnCount})`,
    },
});

/**
 * Shared by payments pages (schedule-payment, upcoming-payments) that page an already-computed
 * row list client-side: slices `rows` to the current page, treating `rowsPerPage === -1` as
 * "show all" (matches each page's prior inline implementation).
 */
export const paginateRows = <T>(rows: T[], currentPage: number, rowsPerPage: number): T[] => {
    const startIndex = currentPage * rowsPerPage;
    const endIndex = rowsPerPage === -1 ? rows.length : startIndex + rowsPerPage;
    return rows.slice(startIndex, endIndex);
};

export const mapPaymentHistoryRow = (
    payment: PaymentHistoryItemNew,
    formatToUSDate: (date: string) => string,
    formatCurrency: (amount: number) => string,
): PaymentHistoryRowBase => {
    const paymentAmount = Number(payment.amount) || 0;
    const hasInvoices = payment.invoices && payment.invoices.length > 0;
    const firstInvoiceStatus = hasInvoices ? payment.invoices[0].status : '';
    const hasReturnedInvoice = hasInvoices && payment.invoices.some(inv => inv.status === 'Returned');
    const firstInvoiceNumber = hasInvoices ? payment.invoices[0].invoiceHeaderOId : '';

    return {
        contractId: payment.contractId || 'N/A',
        paymentDate: payment.paymentDate ? formatToUSDate(payment.paymentDate) : 'N/A',
        invoiceNumber: firstInvoiceNumber || 'N/A',
        referenceNumber: payment.referenceNumber || 'N/A',
        paymentType: payment.paymentType || 'N/A',
        amount: formatCurrency(paymentAmount),
        invoices: payment.invoices || [],
        invoiceCount: payment.invoices?.length || 0,
        status: hasReturnedInvoice ? 'Returned' : firstInvoiceStatus,
        originalPayment: payment,
    };
};
